import React, { useEffect, useState, RefObject } from 'react';
import ReactDOM from 'react-dom';
import { observer } from 'mobx-react';
import useStore from '../../helpers/useStore';
import { CalendarContext } from '../../stores/Calendar/calendar.provider';

interface TimeSlotIndicatorProps {
  calendarRootRef: RefObject<HTMLDivElement>;
}

/**
 * A mouse-driven time slot indicator component that displays:
 * - A horizontal red line across the calendar at the hovered time slot
 * - A time label that follows the mouse cursor
 * 
 * Observes CalendarStore for mouseHoverTimeSlot, isMouseHover, and originalDragTime.
 * This component is decoupled from keyboard navigation (StickyTimeSlotLabel uses highlightedTimeSlot).
 */
const TimeSlotIndicator: React.FC<TimeSlotIndicatorProps> = observer(({
  calendarRootRef,
}) => {
  // Get state from CalendarStore (mobx observable)
  const CalendarStore = useStore(CalendarContext);
  const { mouseHoverTimeSlot, isMouseHover, originalDragTime } = CalendarStore;

  const [mousePosition, setMousePosition] = useState<{ x: number; y: number } | null>(null);
  const [isHoveringEvent, setIsHoveringEvent] = useState(false);
  const [linePosition, setLinePosition] = useState<number | null>(null);

  // Track mouse position for time label
  useEffect(() => {
    const root = calendarRootRef.current;
    if (!root) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (isMouseHover) {
        const rootRect = root.getBoundingClientRect();
        setMousePosition({
          x: e.clientX - rootRect.left,
          y: e.clientY - rootRect.top
        });
      }
    };

    const handleMouseLeave = () => {
      setMousePosition(null);
    };

    root.addEventListener('mousemove', handleMouseMove);
    root.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      root.removeEventListener('mousemove', handleMouseMove);
      root.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [calendarRootRef, isMouseHover]);

  // Track when mouse hovers over events to hide indicators
  useEffect(() => {
    const root = calendarRootRef.current;
    if (!root) return;

    const handleEventMouseEnter = () => setIsHoveringEvent(true);
    const handleEventMouseLeave = () => setIsHoveringEvent(false);

    // Add listeners to all event elements
    const addEventListeners = () => {
      const events = root.querySelectorAll('.fc-event');
      events.forEach(event => {
        event.addEventListener('mouseenter', handleEventMouseEnter);
        event.addEventListener('mouseleave', handleEventMouseLeave);
      });
    };

    // Initial setup
    addEventListeners();

    // Re-attach when events change (use MutationObserver)
    const mutationObserver = new MutationObserver(addEventListeners);
    mutationObserver.observe(root, { childList: true, subtree: true });

    return () => {
      mutationObserver.disconnect();
      const events = root.querySelectorAll('.fc-event');
      events.forEach(event => {
        event.removeEventListener('mouseenter', handleEventMouseEnter);
        event.removeEventListener('mouseleave', handleEventMouseLeave);
      });
    };
  }, [calendarRootRef]);

  // Calculate line position when time slot changes
  useEffect(() => {
    const root = calendarRootRef.current;
    if (!root || !mouseHoverTimeSlot) {
      setLinePosition(null);
      return;
    }

    const cell = root.querySelector(`.fc-timegrid-slot-resource[data-time="${mouseHoverTimeSlot}"]`) as HTMLElement;
    if (cell) {
      const rootRect = root.getBoundingClientRect();
      const cellRect = cell.getBoundingClientRect();
      const topPosition = cellRect.top - rootRect.top + root.scrollTop;
      setLinePosition(topPosition);
    } else {
      setLinePosition(null);
    }
  }, [calendarRootRef, mouseHoverTimeSlot]);

  // Don't render if conditions aren't met
  if (!mouseHoverTimeSlot || !isMouseHover || isHoveringEvent || !mousePosition || linePosition === null) {
    return null;
  }

  // Format time for display (remove seconds)
  const timeStr = mouseHoverTimeSlot.substring(0, 5); // HH:mm
  const labelOffset = 10; // offset from cursor

  // Render using portal to the calendar root element
  const root = calendarRootRef.current;
  if (!root) return null;

  // Ensure root has relative positioning for absolute children
  if (root.style.position !== 'relative') {
    root.style.position = 'relative';
  }

  return ReactDOM.createPortal(
    <>
      {/* Horizontal line indicator */}
      <div
        className="time-slot-indicator-line"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: `${linePosition}px`,
          height: '2px',
          backgroundColor: '#9C1A35',
          pointerEvents: 'none',
          zIndex: 5,
          transition: 'top 0.1s ease-out',
          opacity: 0.7,
        }}
      />
      {/* Time label that follows cursor */}
      <div
        className="time-slot-indicator-label"
        style={{
          position: 'absolute',
          left: `${mousePosition.x + labelOffset}px`,
          top: `${linePosition}px`,
          backgroundColor: '#9C1A35',
          color: 'white',
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 'bold',
          pointerEvents: 'none',
          zIndex: 6,
          whiteSpace: 'nowrap',
          transition: 'top 0.1s ease-out, left 0.1s ease-out',
          transform: 'translateY(-50%)',
        }}
      >
        {timeStr}
      </div>
    </>,
    root
  );
});

export default TimeSlotIndicator;
